'use client';

import {
  AIConversation,
  AIConversationContent,
  AIConversationScrollButton,
} from '@ahafood/ui/components/ai/conversation';
import {
  AIInput,
  AIInputSubmit,
  AIInputTextarea,
} from '@ahafood/ui/components/ai/input';
import {
  AIMessage,
  AIMessageAvatar,
  AIMessageContent,
} from '@ahafood/ui/components/ai/message';
import {
  AISuggestion,
  AISuggestions,
} from '@ahafood/ui/components/ai/suggestion';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Badge } from '@ahafood/ui/components/badge';
import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { cn } from '@ahm/common-helpers';
import { useChat } from '@ai-sdk/react';
import { TextStreamChatTransport } from 'ai';
import { getCookie } from 'cookies-next';
// import { Sparkles } from 'lucide-react';
import Image from 'next/image';
import { type FormEventHandler, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { defaultSuggestionsValue } from '../(main)/store/components/chatbot/chatbot';
import type { ItemList } from './chatbot-type';
import {
  useChatbotInteracted,
  chatbotInteractedFn,
} from '@/api/use-chatbot-interacted';
import { useGetSuggestQuestions } from '@/api/use-get-suggest-questions';
import { useGoToOrderWebstore } from '@/api/use-webstore-order';
import { ROUTES } from '@/config/router';
import { useRouter } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useAiFoodOrderStore } from '@/store/use-ai-store';
import { useAuthStore } from '@/store/use-auth-store';
import type { MenuItem } from '@/types/item';

type ChatboxProps = {
  className?: string;
  threadId: string;
};

export default function Chat({ threadId, className }: ChatboxProps) {
  // const { handleChatClick } = useMessenger();
  const token = String(getCookie('ws_token') ?? '');
  const router = useRouter();
  const { user } = useAuthStore();
  const cityId = useAiFoodOrderStore.use.city_id();
  const address = useAiFoodOrderStore.use.address();
  const [text, setText] = useState<string>('');
  const [status, setStatus] = useState<
    'submitted' | 'streaming' | 'ready' | 'error'
  >('ready');
  // Lưu trạng thái 'xem thêm' cho từng assistant message
  const [showAllItemsMap, setShowAllItemsMap] = useState<
    Record<string, boolean>
  >({});
  const goToOrderWebstore = useGoToOrderWebstore();

  // Sử dụng useGetSuggestQuestions để lấy suggestions từ API
  const { data: suggestQuestionsData, refetch: refetchSuggestions } =
    useGetSuggestQuestions(
      user?.cunstomerId && cityId
        ? {
            customer_id: user.cunstomerId,
            city_id: cityId,
          }
        : undefined
    );

  const convertToMenuItem = (item: ItemList | null): MenuItem | null => {
    if (!item) return null;

    const { _id, name, price, discount_price, image_thumb_url = '' } = item;
    return {
      _id,
      name,
      price,
      discounted_price: discount_price,
      image_thumb_url,
    };
  };

  const { mutate: chatbotInteracted } = useChatbotInteracted({
    mutationFn: (params) =>
      chatbotInteractedFn(params, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      }),
  });

  const handleChat = async (item: ItemList) => {
    try {
      if (item.store_id) {
        chatbotInteracted({ store_id: item.store_id });
      }
      // goToOrderWebstore sẽ tự động gọi openSheet với store context
      await goToOrderWebstore(
        item.page_id.toString(),
        item.slug ?? '',
        item.nearest_branch.branch_id ?? '',
        convertToMenuItem(item)!,
        item.store_id.toString() ?? ''
      );
      router.push(ROUTES.storeDetail(item.slug ?? ''));
      // Close AmoriContainer after navigation
      if (
        typeof window !== 'undefined' &&
        typeof window.setOpenChange === 'function'
      ) {
        window.setOpenChange(false);
      }
    } catch (error) {
      console.error('Failed to go to order webstore:', error);
    }
  };

  // Key for storing messages in localStorage
  function getThreadMessagesKey(threadId: string) {
    return `ahafood.chat.messages.${threadId}`;
  }

  // Lưu threadId và lastMessageTime vào localStorage khi có tin nhắn mới
  type ThreadInfo = {
    threadId: string;
    userId: string;
    lastMessageTime: number;
  };
  const updateThreadSession = (time: number) => {
    if (threadId && user?.id) {
      const key = 'ahafood.chat.thread';
      const raw = localStorage.getItem(key);
      let info: ThreadInfo | null = null;
      if (raw) {
        try {
          info = JSON.parse(raw) as ThreadInfo;
        } catch {
          /* empty */
        }
      }
      if (!info || info.threadId !== threadId || info.userId !== user.id) {
        localStorage.setItem(
          key,
          JSON.stringify({ threadId, userId: user.id, lastMessageTime: time })
        );
      } else {
        info.lastMessageTime = time;
        localStorage.setItem(key, JSON.stringify(info));
      }
    }
  };

  const { messages, sendMessage, setMessages } = useChat({
    transport: new TextStreamChatTransport({
      api: process.env.NEXT_PUBLIC_RECOMMENDATION_API,
      prepareSendMessagesRequest: ({ id, messages }) => {
        const lastMessage = messages[messages.length - 1];
        return {
          body: {
            question:
              lastMessage?.parts[0] && lastMessage.parts[0].type === 'text'
                ? (lastMessage.parts[0] as { type: 'text'; text: string }).text
                : '',
            thread_id: threadId ?? id,
            customer_id: user?.cunstomerId,
            city_id: cityId,
            lat: address?.lat,
            lng: address?.lng,
            limit: 20,
          },
        };
      },
    }),
    onData: () => {
      setStatus('ready');
      updateThreadSession(Date.now());
    },
    onFinish: () => {
      setStatus('ready');
      updateThreadSession(Date.now());
    },
    onError: () => {
      setStatus('error');
    },
    // State to store messages (for restoring from localStorage)
  });

  const defaultSuggestions = useMemo(() => {
    if (suggestQuestionsData?.suggest_question?.length) {
      return suggestQuestionsData.suggest_question;
    }
    return defaultSuggestionsValue;
  }, [suggestQuestionsData]);

  const handleSuggestionClick = (suggestion: string) => {
    // Tracking: user bắt đầu chat với bot qua suggestion
    if (messages.filter((msg) => msg.role === 'user').length === 0) {
      trackEvent('chatbot_event', {
        user_id: user?.cunstomerId,
        thread_id: threadId,
        timestamp: Date.now(),
        button_name: 'chatbot_suggestion',
      });
    }
    setText(suggestion);
    setStatus('submitted');
    setTimeout(() => {
      setStatus('streaming');
    }, 200);
    void sendMessage({ text: suggestion });
    setText('');
  };

  // Handle form submit (send message)
  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (!text) return;

    if (messages.filter((msg) => msg.role === 'user').length === 0) {
      trackEvent('chatbot_event', {
        user_id: user?.cunstomerId,
        button_name: 'chatbot_start',
        thread_id: threadId,
        timestamp: Date.now(),
      });
    }

    setStatus('submitted');
    setTimeout(() => {
      setStatus('streaming');
    }, 200);
    void sendMessage({ text });
    setText('');
  };

  useEffect(() => {
    if (threadId && cityId) {
      void refetchSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, cityId]);

  // Persist messages to localStorage when they change and threadId exists
  useEffect(() => {
    if (threadId && messages.length > 0) {
      try {
        localStorage.setItem(
          getThreadMessagesKey(threadId),
          JSON.stringify(messages)
        );
      } catch {
        /* empty */
      }
    }
  }, [messages, threadId]);

  // Restore messages from localStorage when threadId changes (only if messages is empty)
  useEffect(() => {
    if (threadId && setMessages) {
      const key = getThreadMessagesKey(threadId);
      const raw = localStorage.getItem(key);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setMessages(parsed);
          }
        } catch {
          /* empty */
        }
      }
    }
  }, [threadId, setMessages]);

  // Check and auto-send suggestion from AmoriBanner
  useEffect(() => {
    if (threadId) {
      const autoSuggestion = sessionStorage.getItem(
        'ahafood.chat.autoSuggestion'
      );
      if (
        autoSuggestion &&
        messages.filter((msg) => msg.role === 'user').length === 0
      ) {
        sessionStorage.removeItem('ahafood.chat.autoSuggestion');
        setTimeout(() => {
          handleSuggestionClick(autoSuggestion);
        }, 500);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  return (
    <AIConversation
      className={cn(
        'flex flex-col size-full bg-white -mt-6 rounded-t-3xl z-20',
        className
      )}
    >
      <AIConversationContent className="flex-1 pb-12">
        {/* Show greeting card and default suggestions only if there are no user messages */}
        {messages.filter((msg) => msg.role === 'user').length === 0 && (
          <div>
            <Card className="flex flex-col items-center gap-2 bg-gradient-to-b from-white via-white to-red-20/60 rounded-xl mt-6 py-6 px-4 border-none shadow-md justify-center relative overflow-y-visible pt-10">
              <div className="absolute top-3 right-3">
                <Image
                  src="/static/images/ai-icon.png"
                  alt="Chatbot Detail"
                  width={33}
                  height={24}
                />
              </div>
              <div className="flex">
                <div className="relative size-20 flex-none">
                  <Image
                    src="/static/images/chatbot-detail.webp"
                    alt="AI Bot"
                    fill
                    className="object-contain"
                  />
                </div>
                <div className="">
                  {/* <div className="bg-gradient-to-b from-primary-50 via-[#A80036] to-secondary-60 bg-clip-text text-transparent text-xs flex items-center gap-2 line-clamp-2 flex-wrap">
                    <span className="font-semibold">
                      Đặt đơn cùng Amori để được{' '}
                    </span>
                    <Badge className="text-primary-60 bg-yellow-30 text-xs p-0.5 font-bold">
                      FREESHIP 100%
                    </Badge>
                  </div> */}
                  <div className="flex text-xs items-center font-medium bg-gradient-to-b from-primary-50 via-[#a82200] to-secondary-50 bg-clip-text text-transparent w-fit">
                    Một mình? Có Amori! Một bữa có{' '}
                    <Image
                      width={71}
                      height={21}
                      className="ml-1"
                      src={'/static/images/solo-meal.png'}
                      alt={'Solo Meal'}
                    />
                  </div>
                  <div className="text-2xl font-bold mt-1 bg-gradient-to-r from-[#A80036] via-red-40 to-secondary-50 bg-clip-text text-transparent w-fit">
                    {user?.name
                      ? user.name === 'Ahamove User'
                        ? 'Bạn'
                        : user.name
                      : 'Bạn'}{' '}
                    muốn ăn gì?
                  </div>
                </div>
              </div>
              <div className="flex w-full justify-start gap-2 font-normal">
                <Button
                  variant="outline"
                  onClick={() => {
                    handleSuggestionClick('Solo Meal');
                  }}
                  className="bg-white/30 border-2 border-white w-full px-2 rounded-lg hover:bg-white/50 font-light"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Solo Meal
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    handleSuggestionClick('Món có deal');
                  }}
                  className="bg-white/30 border-2 border-white w-full px-2 rounded-lg hover:bg-white/50 font-light"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Món có deal 🔥
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    handleSuggestionClick('Món ngẫu nhiên');
                  }}
                  className="bg-white/30 border-2 border-white w-full px-2 rounded-lg hover:bg-white/50 font-light"
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Món ngẫu nhiên
                </Button>
              </div>
            </Card>
            <div className="pt-4">
              <div className="text-lg text-neutral-70 font-bold mb-2 mt-6">
                Gợi ý dành cho bạn:
              </div>
              <div className="space-y-3">
                {defaultSuggestions.map((suggestion, i) => (
                  <Button
                    key={i}
                    className="rounded-xl h-auto shadow-sm bg-white hover:bg-white border border-neutral-10 flex items-start flex-col px-3 py-2 cursor-pointer hover:shadow-md gap-2 w-full"
                    onClick={() => handleSuggestionClick(suggestion)}
                    disabled={status === 'streaming' || status === 'submitted'}
                  >
                    <Badge
                      variant="secondary"
                      className="flex items-center text-xs pl-1 py-1 bg-gradient-to-r from-blue-60 via-primary-50 to-primary-50 bg-clip-text text-transparent"
                    >
                      <Image
                        src="/static/images/new-ai.png"
                        alt="AI Bot"
                        width={16}
                        height={16}
                        className="size-4 mr-1 object-contain"
                      />
                      AI Đề xuất
                    </Badge>
                    <span className="text-sm font-thin text-left text-neutral-90">
                      {suggestion}
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}
        {messages.map((msg) => {
          if (msg.role === 'user') {
            return (
              <AIMessage from="user" key={msg.id}>
                <AIMessageContent>
                  {(msg.parts[0] as { type: 'text'; text: string })?.text}
                </AIMessageContent>
                {user?.picture && (
                  <AIMessageAvatar
                    src={user?.picture}
                    name={user?.name}
                    className="bg-neutral-10 size-10"
                  />
                )}
              </AIMessage>
            );
          }

          if (msg.role === 'assistant') {
            const parsed = parseBotMessages(msg.parts);
            // Trạng thái xem thêm cho từng message
            const showAllItems = showAllItemsMap[msg.id] || false;
            return (
              <div
                key={msg.id}
                className="space-y-2 flex flex-col items-start gap-2"
              >
                {/* Avatar for assistant */}
                <Badge
                  variant="secondary"
                  className="py-1 flex items-center gap-1 font-bold border bg-white shadow-md"
                >
                  <Image
                    src="/static/images/new-ai.png"
                    alt="AI Bot"
                    width={16}
                    height={16}
                    className="size-4 mr-1 object-contain"
                  />
                  <span className="bg-gradient-to-r from-blue-50 to-secondary-60 inline-block text-transparent bg-clip-text pr-0.5 font-bold">
                    Amori
                  </span>
                </Badge>

                <div className="flex line-clamp-2 flex-wrap items-center bg-gradient-to-r from-primary-10 to-secondary-10 text-neutral-90 rounded-full px-4 py-2 text-xs gap-0.5">
                  <Image
                    src="/static/images/new-bot-avt.webp"
                    alt="Amori"
                    width={24}
                    height={24}
                    className="size-6 object-contain"
                  />
                  <span className="font-bold">"Đặt ngay"</span> để nhận{' '}
                  <Badge className="bg-white text-primary-50 font-bold py-0 text-xs px-1 text-center">
                    FREESHIP 100%
                  </Badge>
                  <span className="font-bold">
                    của{' '}
                    <span className="bg-gradient-to-r from-blue-50 to-secondary-60 inline-block text-transparent bg-clip-text pr-0.5 font-bold">
                      Amori
                    </span>{' '}
                    nhé!
                  </span>
                </div>
                <div className="w-full space-y-2">
                  {parsed.items.length > 0 && (
                    <div className="w-full space-y-2">
                      <div className="flex flex-col gap-3 overflow-y-auto pb-2">
                        {(showAllItems
                          ? parsed.items
                          : parsed.items.slice(0, 4)
                        ).map((item) => (
                          <Card
                            key={item._id}
                            className="shrink-0 flex w-full h-auto rounded-xl overflow-hidden border border-border cursor-pointer p-2 gap-3"
                            onClick={() => {
                              if (item.store_id) {
                                chatbotInteracted({ store_id: item.store_id });
                              }
                              if (item.slug) {
                                router.push(ROUTES.storeDetail(item.slug));
                                if (
                                  typeof window !== 'undefined' &&
                                  typeof window.setOpenChange === 'function'
                                ) {
                                  window.setOpenChange(false);
                                }
                              }
                              trackEvent('chatbot_event', {
                                button_name: 'chatbot_item_click',
                                timestamp: Date.now(),
                                user_id: user?.cunstomerId,
                                thread_id: threadId,
                              });
                            }}
                          >
                            <div className="w-[100px] h-full bg-newtral-10">
                              <Avatar className="size-full rounded-lg">
                                <AvatarImage
                                  className="object-cover size-full"
                                  src={
                                    item.image_thumb_url ||
                                    '/static/images/empty-store-bot.webp'
                                  }
                                />
                                <AvatarFallback>{item.name}</AvatarFallback>
                              </Avatar>
                            </div>
                            <div className="flex flex-col justify-between p-0 flex-1 gap-2">
                              <div>
                                <div className="font-semibold text-sm line-clamp-2">
                                  {item.name}
                                </div>
                                <div className="text-xs text-neutral-50 line-clamp-2">
                                  {item.page_name}
                                </div>
                              </div>
                              <div className="flex w-full justify-between">
                                <div>
                                  <div className="text-base font-bold text-primary">
                                    {item.discount_price ? (
                                      <div className="flex flex-col gap-1">
                                        <span className="line-through text-neutral-40 mr-2 text-sm">
                                          {item.price.toLocaleString()} đ
                                        </span>
                                        <span className="text-primary font-bold">
                                          {item.discount_price.toLocaleString()}{' '}
                                          đ
                                        </span>
                                      </div>
                                    ) : (
                                      <>{item.price.toLocaleString()} đ</>
                                    )}
                                  </div>
                                  <div className="text-xs text-neutral-40">
                                    Cách bạn ~ {item.distance.toFixed(1)} km
                                  </div>
                                </div>
                                <Button
                                  onClick={() => handleChat(item)}
                                  className="md:min-w-32 bg-primary-50"
                                >
                                  Đặt ngay
                                </Button>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>

                      {parsed.items.length > 4 && (
                        <div className="flex justify-center">
                          <Button
                            variant="ghost"
                            onClick={() =>
                              setShowAllItemsMap((prev) => ({
                                ...prev,
                                [msg.id]: !showAllItems,
                              }))
                            }
                          >
                            {showAllItems ? 'Ẩn bớt' : 'Xem thêm'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  <AIMessage from="assistant">
                    <div className="flex gap-2">
                      <AIMessageContent>
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => {
                              // Handle text with quotes to make them bold
                              if (typeof children === 'string') {
                                const parts = children.split(/("[^"]*")/);
                                return (
                                  <p className="mb-2 last:mb-0">
                                    {parts.map((part, index) => {
                                      if (
                                        part.startsWith('"') &&
                                        part.endsWith('"')
                                      ) {
                                        return (
                                          <strong
                                            key={index}
                                            className="font-bold text-neutral-90"
                                          >
                                            {part}
                                          </strong>
                                        );
                                      }
                                      return part;
                                    })}
                                  </p>
                                );
                              }
                              return (
                                <p className="mb-2 last:mb-0">{children}</p>
                              );
                            },
                            strong: ({ children }) => (
                              <strong className="font-bold text-neutral-90">
                                {children}
                              </strong>
                            ),
                            em: ({ children }) => (
                              <em className="italic">{children}</em>
                            ),
                            ul: ({ children }) => (
                              <ul className="list-disc list-inside mb-2 space-y-1">
                                {children}
                              </ul>
                            ),
                            ol: ({ children }) => (
                              <ol className="list-decimal list-inside mb-2 space-y-1">
                                {children}
                              </ol>
                            ),
                            li: ({ children }) => (
                              <li className="text-sm">{children}</li>
                            ),
                            blockquote: ({ children }) => (
                              <blockquote className="border-l-4 border-primary-20 pl-4 py-2 my-2 bg-neutral-5 rounded-r-lg">
                                {children}
                              </blockquote>
                            ),
                            code: ({ children }) => (
                              <code className="bg-neutral-10 px-1.5 py-0.5 rounded text-sm font-mono">
                                {children}
                              </code>
                            ),
                            pre: ({ children }) => (
                              <pre className="bg-neutral-10 p-3 rounded-lg overflow-x-auto my-2">
                                {children}
                              </pre>
                            ),
                            h1: ({ children }) => (
                              <h1 className="text-lg font-bold mb-2">
                                {children}
                              </h1>
                            ),
                            h2: ({ children }) => (
                              <h2 className="text-base font-bold mb-2">
                                {children}
                              </h2>
                            ),
                            h3: ({ children }) => (
                              <h3 className="text-sm font-bold mb-1">
                                {children}
                              </h3>
                            ),
                            a: ({ href, children }) => (
                              <a
                                href={href}
                                className="text-primary-50 underline hover:text-primary-60"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {parsed.answer
                            .trim()
                            .replace(/\\n/g, '\n')
                            .replace(/\\\\/g, '\\')}
                        </ReactMarkdown>
                      </AIMessageContent>
                      <Image
                        src="/static/images/new-ai.png"
                        alt="AI Bot"
                        width={16}
                        height={16}
                        className="size-4 mr-1 object-contain"
                      />
                    </div>
                  </AIMessage>

                  {/* Suggestions */}
                  {parsed.suggestions.length > 0 && (
                    <AISuggestions className="flex flex-wrap gap-2 w-full items-start">
                      {parsed.suggestions.map((suggestion, i) => (
                        <AISuggestion
                          key={i}
                          onClick={handleSuggestionClick}
                          suggestion={suggestion}
                          disabled={
                            status === 'streaming' || status === 'submitted'
                          }
                          className="break-words whitespace-normal text-left size-fit max-w-full inline-block rounded-2xl py-1"
                        />
                      ))}
                    </AISuggestions>
                  )}
                </div>
              </div>
            );
          }

          return null;
        })}
        {(status === 'submitted' || status === 'streaming') &&
          (!messages.length ||
            messages[messages.length - 1].role !== 'assistant') && (
            <div className="flex items-center gap-2 px-4 py-2">
              <Avatar className="size-14 bg-blue-50">
                <AvatarImage
                  src="/static/images/loading-gif.webp"
                  alt="AI Bot"
                />
                <AvatarFallback>AI</AvatarFallback>
              </Avatar>
              {/* <div className="flex-1">
                <div className="h-4 bg-blue-10 rounded w-1/2 mb-2" />
                <div className="h-3 bg-blue-50 rounded w-1/3" />
              </div> */}
            </div>
          )}
      </AIConversationContent>
      <AIConversationScrollButton className="bottom-32" />
      <div className="px-4 pb-4 pt-2">
        <AIInput
          onSubmit={handleSubmit}
          className="relative shadow-md border-none rounded-xl bg-neutral-10/70"
        >
          <AIInputTextarea
            className="pr-14 h-10"
            placeholder="Ăn gì nào bạn ơi?"
            onChange={(e) => setText(e.target.value)}
            value={text}
          />
          <AIInputSubmit
            className="absolute top-1/2 right-3 -translate-y-1/2 text-red-50 hover:text-red-60 bg-white hover:bg-neutral-10 transition-colors duration-200"
            disabled={!text || status === 'streaming' || status === 'submitted'}
            status={status}
          />
        </AIInput>
      </div>
    </AIConversation>
  );
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function parseBotMessages(parts: { type: string; text?: string }[]) {
  const result = {
    answer: '',
    suggestions: [] as string[],
    items: [] as ItemList[],
  };

  const suggestionMap = new Map<string, string>();
  let combined = '';

  // Join all streamed parts
  for (const part of parts) {
    if (part.type === 'text' && part.text) {
      combined += part.text;
    }
  }

  // Split by line and look for event/data pairs
  const lines = combined.split('\n');
  let currentEventType: string | null = null;

  for (const line of lines) {
    if (line.startsWith('event: ')) {
      currentEventType = line.slice(7); // keep raw name
    } else if (line.startsWith('data: ') && currentEventType) {
      const rawData = line.slice(6); // preserve spacing

      try {
        if (currentEventType === 'answer') {
          result.answer += rawData;
        } else if (currentEventType.startsWith('suggest_')) {
          const prev = suggestionMap.get(currentEventType) ?? '';
          suggestionMap.set(currentEventType, prev + rawData);
        } else if (currentEventType === 'tool_data') {
          const parsed = JSON.parse(rawData);
          result.items = parsed.item_list || [];
        }
      } catch (e) {
        console.warn('Failed to parse', { currentEventType, rawData }, e);
      }
    }
  }

  result.answer = normalizeText(result.answer);
  result.suggestions = Array.from(suggestionMap.values()).map(normalizeText);

  return result;
}

function normalizeText(text: string): string {
  return text
    .replace(/\s+/g, ' ') // normalize multiple spaces
    .replace(/ ?([,.!?])/g, '$1') // remove space before punctuation
    .trim();
}
